import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  TablePagination,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { clientService } from '../../../shared/services/api/clientService';
import { IConversationSummary, whatsAppService } from '../../../shared/services/api/whatsapp';
import { IPropsBusinessPage } from '../Business';

/** Normaliza telefone para dígitos puros para comparação. */
const digitsOnly = (s: string) => s.replace(/\D/g, '');

/** Formata +5511999999999 → (11) 99999-9999 */
const formatPhone = (phone: string) => {
  const d = digitsOnly(phone);
  if (d.length === 13) {
    return `+${d[0]} (${d[1]}${d[2]}) ${d[3]}${d[4]}${d[5]}${d[6]}${d[7]}-${d[8]}${d[9]}${d[10]}${d[11]}${d[12]}`;
  }
  return phone;
};

export const WhatsAppBotHistory: React.FC<IPropsBusinessPage> = ({ business }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedPhone, setExpandedPhone] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['whatsapp-history', business.id],
    queryFn: () => whatsAppService.getHistory(business.id, 200),
    refetchInterval: 30_000,
  });

  // Busca clientes do business para vincular telefone → nome
  const { data: clients } = useQuery({
    queryKey: ['business-clients', business.id],
    queryFn: () => clientService.searchClients(business.id),
  });

  /** Mapa: dígitos do telefone → nome do cliente (quando houver match). */
  const clientNameByPhone = useMemo(() => {
    const map = new Map<string, string>();
    if (!clients) return map;
    for (const c of clients) {
      if (!c.phone) continue;
      const key = digitsOnly(c.phone);
      if (key) map.set(key, c.name);
    }
    return map;
  }, [clients]);

  /** Encontra nome do cliente a partir do telefone do WhatsApp (+55…). */
  const findClientName = (wppPhone: string): string | null => {
    const wppDigits = digitsOnly(wppPhone);
    const entries = Array.from(clientNameByPhone.entries());
    for (let i = 0; i < entries.length; i++) {
      const [clientDigits, name] = entries[i];
      // Match: o número do cliente está contido no final do WhatsApp (com ou sem DDI)
      if (wppDigits.endsWith(clientDigits) || clientDigits.endsWith(wppDigits)) {
        return name;
      }
    }
    return null;
  };

  const conversations = data?.conversations ?? [];

  const visibleConversations = conversations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (isFetching) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant='text' width={240} height={32} sx={{ mb: 1 }} />
          <Divider sx={{ mb: 2 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Skeleton variant='circular' width={40} height={40} />
              <Box flex={1}>
                <Skeleton variant='text' width='60%' height={20} />
                <Skeleton variant='text' width='90%' height={16} />
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant='h6' gutterBottom>Histórico de Conversas</Typography>
        <Divider sx={{ mb: 2 }} />

        {!isFetching && conversations.length === 0 && (
          <Typography color='text.secondary' textAlign='center' py={4}>
            Nenhuma conversa encontrada. As primeiras mensagens aparecerão aqui
            após clientes interagirem com o bot.
          </Typography>
        )}

        {conversations.length > 0 && (
          <>
            <List disablePadding>
              {visibleConversations.map((conv: IConversationSummary) => {
                const clientName = findClientName(conv.phoneNumber);
                const isExpanded = expandedPhone === conv.phoneNumber;
                return (
                  <Box key={conv.phoneNumber}>
                    <ListItem
                      divider={!isExpanded}
                      disablePadding
                      secondaryAction={
                        <IconButton
                          size='small'
                          onClick={() =>
                            setExpandedPhone(isExpanded ? null : conv.phoneNumber)
                          }
                        >
                          <Icon>{isExpanded ? 'expand_less' : 'expand_more'}</Icon>
                        </IconButton>
                      }
                    >
                      <ListItemButton
                        onClick={() =>
                          setExpandedPhone(isExpanded ? null : conv.phoneNumber)
                        }
                      >
                        <ListItemText
                          primary={
                            <Box display='flex' alignItems='center' gap={1}>
                              <Typography variant='body2' fontWeight='medium'>
                                {clientName ?? formatPhone(conv.phoneNumber)}
                              </Typography>
                              {clientName && (
                                <Typography variant='caption' color='text.disabled'>
                                  {formatPhone(conv.phoneNumber)}
                                </Typography>
                              )}
                              <Chip
                                label={`${conv.messageCount} msg`}
                                size='small'
                                variant='outlined'
                              />
                            </Box>
                          }
                          secondary={
                            <Box component='span' display='block'>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                noWrap
                                sx={{ maxWidth: 400 }}
                              >
                                {conv.lastMessage}
                              </Typography>
                              <Typography variant='caption' color='text.disabled'>
                                {new Date(conv.lastMessageAt).toLocaleString('pt-BR')}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>

                    {/* Mensagens expandidas */}
                    <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                      <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1 }}>
                        {(conv.messages ?? []).length === 0 && (
                          <Typography variant='body2' color='text.secondary' textAlign='center' py={1}>
                            Detalhes não disponíveis.
                          </Typography>
                        )}
                        {(conv.messages ?? []).map((msg) => {
                          // createdAt vem como UTC (sem timezone) do backend
                          // Força parsing como UTC e converte pra BRT
                          const msgDate = new Date(
                            msg.createdAt.endsWith('Z') ? msg.createdAt : msg.createdAt + 'Z'
                          );
                          const timeStr = msgDate.toLocaleTimeString('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                          return (
                            <Box
                              key={msg.id}
                              sx={{
                                mb: 1,
                                display: 'flex',
                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: '85%',
                                  bgcolor: (theme) =>
                                    msg.role === 'user'
                                      ? 'primary.main'
                                      : msg.role === 'tool'
                                        ? theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'
                                        : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                                  color: msg.role === 'user' ? '#fff' : 'text.primary',
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 0.8,
                                }}
                              >
                                <Typography variant='caption' fontWeight='bold' display='block'>
                                  {msg.role === 'user'
                                    ? 'Cliente'
                                    : msg.role === 'tool'
                                      ? 'Ferramenta'
                                      : 'Bot'}
                                </Typography>
                                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {msg.content.length > 300
                                    ? `${msg.content.slice(0, 300)}…`
                                    : msg.content}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  color={msg.role === 'user' ? 'rgba(255,255,255,0.7)' : 'text.disabled'}
                                  display='block'
                                  textAlign='right'
                                >
                                  {timeStr}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                      <Divider />
                    </Collapse>
                  </Box>
                );
              })}
            </List>

            <TablePagination
              component='div'
              count={conversations.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage='Por página:'
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
